/**
 * Widget Renderer Component
 * Renders the appropriate widget based on type
 */

import { Widget, WidgetData } from '../../types';
import {
  KPIWidget,
  StatusIndicatorWidget,
  StatusGridWidget,
  TableWidget,
  ChartBarWidget,
  ChartLineWidget,
  ChartPieWidget,
  SyncStatusWidget,
  type SyncLogEntry,
} from '../widgets';
import { PlanningScenarioWidget } from '../widgets/PlanningScenarioWidget';
import { CapacityHeatmapWidget } from '../widgets/CapacityHeatmapWidget';
import { ResourceTimelineWidget } from '../widgets/ResourceTimelineWidget';
import { ConflictListWidget } from '../widgets/ConflictListWidget';
import { ScenarioComparisonWidget } from '../widgets/ScenarioComparisonWidget';
import { GanttChartWidget } from '../widgets/GanttChartWidget';
import type { ScenarioSummary } from '../../hooks/usePlanningScenarios';
import type { HeatmapResponse, UtilizationResponse } from '../../hooks/useCapacityData';
import type { GanttResponse, GanttOperation } from '../../hooks/useGanttData';
import type { PlanningConflict } from '../../hooks/useConflicts';
import type { ComparisonResponse } from '../../hooks/useScenarioComparison';

interface WidgetRendererProps {
  widget: Widget;
  data?: WidgetData;
  loading?: boolean;
  error?: string;
}

export function WidgetRenderer({
  widget,
  data,
  loading,
  error,
}: WidgetRendererProps) {
  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="text-center">
          <p className="text-red-500 text-sm">{error}</p>
          <button
            className="mt-2 text-xs text-[#45bdd6] hover:underline"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const config = widget.config || {};

  switch (widget.widget_type) {
    case 'kpi':
      return (
        <KPIWidget
          value={data?.value}
          label={config.label || widget.name}
          format={config.format}
          trend={data?.trend}
          trendLabel={data?.trendLabel}
          thresholds={config.thresholds}
          loading={loading}
        />
      );

    case 'status_grid':
      return (
        <StatusGridWidget
          items={data?.items || []}
          config={{
            titleField: config.titleField,
            subtitleField: config.subtitleField,
            statusFields: config.statusFields,
            cardStyle: config.cardStyle,
            columnsPerRow: config.columnsPerRow,
          }}
          loading={loading}
        />
      );

    case 'table':
      return (
        <TableWidget
          data={{
            columns: data?.columns || [],
            rows: data?.rows || [],
          }}
          pageSize={config.pageSize || 20}
          showPagination={config.showPagination !== false}
          striped={config.striped !== false}
          loading={loading}
          // Expandable Rows for nested $expand data (e.g. OrderLines)
          expandable={config.expandable}
          // NEW: Incremental Search / Column Filters (like Infor LN)
          showColumnFilters={config.showColumnFilters}
          filterDebounceMs={config.filterDebounceMs}
        />
      );

    case 'chart_bar':
      return (
        <ChartBarWidget
          data={{
            labels: data?.labels || [],
            datasets: data?.datasets || [],
          }}
          config={config}
          loading={loading}
          title={widget.name}
        />
      );

    case 'chart_line':
      return (
        <ChartLineWidget
          data={{
            labels: data?.labels || [],
            datasets: data?.datasets || [],
          }}
          config={config}
          loading={loading}
          title={widget.name}
        />
      );

    case 'chart_pie':
      return (
        <ChartPieWidget
          data={{
            labels: data?.labels || [],
            datasets: data?.datasets || [],
          }}
          config={config}
          loading={loading}
          title={widget.name}
          donut={config.donut}
        />
      );

    case 'status_indicator':
      return (
        <StatusIndicatorWidget
          value={data?.value}
          label={config.label || widget.name}
          status={data?.status || config.status}
          icon={config.icon}
          color={config.color}
          subtitle={data?.subtitle || config.subtitle}
          loading={loading}
        />
      );

    case 'sync_status':
      // SyncStatusWidget for AI Planner sync monitoring
      // Data should be array of SyncLogEntry from /api/planning/sync/latest/
      return (
        <SyncStatusWidget
          data={(data?.syncLogs as SyncLogEntry[] | undefined) || []}
          loading={loading}
          // onTriggerSync and onRefresh will be passed through WidgetData
          onTriggerSync={data?.onTriggerSync as ((entityType?: string) => Promise<void>) | undefined}
          onRefresh={data?.onRefresh as (() => void) | undefined}
        />
      );

    case 'planning_scenarios':
      // PlanningScenarioWidget for AI Planner scenarios overview
      return (
        <PlanningScenarioWidget
          summary={data?.summary as ScenarioSummary | undefined}
          loading={loading}
          onRefresh={data?.onRefresh as (() => void) | undefined}
          onCalculate={data?.onCalculate as ((id: string) => Promise<{ job_id: string }>) | undefined}
          onViewScenario={data?.onViewScenario as ((id: string) => void) | undefined}
          isCalculating={data?.isCalculating as boolean | undefined}
        />
      );

    case 'capacity_heatmap':
      // CapacityHeatmapWidget for work center utilization visualization
      return (
        <CapacityHeatmapWidget
          heatmap={data?.heatmap as HeatmapResponse | undefined}
          utilization={data?.utilization as UtilizationResponse | undefined}
          loading={loading}
          error={data?.error as Error | null | undefined}
          onRefresh={data?.onRefresh as (() => void) | undefined}
          onSelectScenario={data?.onSelectScenario as (() => void) | undefined}
          scenarioId={data?.scenarioId as string | undefined}
        />
      );

    case 'resource_timeline':
      // ResourceTimelineWidget for work center schedule visualization
      return (
        <ResourceTimelineWidget
          data={data?.ganttData as GanttResponse | undefined}
          loading={loading}
          error={data?.error as Error | null | undefined}
          onRefresh={data?.onRefresh as (() => void) | undefined}
          onOperationClick={data?.onOperationClick as ((op: GanttOperation) => void) | undefined}
          onSelectScenario={data?.onSelectScenario as (() => void) | undefined}
          scenarioId={data?.scenarioId as string | undefined}
        />
      );

    case 'conflict_list':
      // ConflictListWidget for planning conflicts and warnings
      return (
        <ConflictListWidget
          conflicts={data?.conflicts as PlanningConflict[] | undefined}
          loading={loading}
          error={data?.error as Error | null | undefined}
          onRefresh={data?.onRefresh as (() => void) | undefined}
          onResolve={data?.onResolve as ((conflictId: string, note: string) => Promise<void>) | undefined}
          onSelectScenario={data?.onSelectScenario as (() => void) | undefined}
          scenarioId={data?.scenarioId as string | undefined}
          scenarioName={data?.scenarioName as string | undefined}
          isResolving={data?.isResolving as boolean | undefined}
        />
      );

    case 'scenario_comparison':
      // ScenarioComparisonWidget for AI plan vs LN plan comparison
      return (
        <ScenarioComparisonWidget
          data={data?.comparisonData as ComparisonResponse | undefined}
          loading={loading}
          error={data?.error as Error | null | undefined}
          onRefresh={data?.onRefresh as (() => void) | undefined}
          onSelectScenario={data?.onSelectScenario as (() => void) | undefined}
          scenarioId={data?.scenarioId as string | undefined}
        />
      );

    case 'gantt_chart':
      return (
        <GanttChartWidget
          data={data?.ganttData as GanttResponse | undefined}
          loading={loading}
          error={data?.error as Error | null | undefined}
          onRefresh={data?.onRefresh as (() => void) | undefined}
          onOperationClick={data?.onOperationClick as ((op: GanttOperation) => void) | undefined}
          onSelectScenario={data?.onSelectScenario as (() => void) | undefined}
          scenarioId={data?.scenarioId as string | undefined}
          height={config.height || 500}
        />
      );

    default:
      return (
        <div className="flex items-center justify-center h-full p-4 text-gray-500">
          <p>Unknown widget type: {widget.widget_type}</p>
        </div>
      );
  }
}
